/**
 * X-axis tick decluttering for time/sequence charts with many categorical
 * ticks (issue #496 — ProgressBurnChart with quarterly milestones spanning
 * many years).
 *
 * `computeXAxisTickPlan` is a pure function so the skip/grouping decision can
 * be unit tested without touching D3 or the DOM. It never changes which
 * bars render (that stays driven by the full milestone list / xScale
 * domain) — it only decides which ticks get a visible label.
 */

/** Minimal milestone shape the tick planner needs. */
export interface TickableMilestone {
  id: string;
  name: string;
  /** ISO date string (e.g. targetDate). Used for year-grouping fallback. */
  date?: string | null;
}

export type TickLabelMode = 'all' | 'skip' | 'year';

export interface XAxisTickPlan {
  /** Milestone ids that should receive a visible tick + label. */
  tickIds: string[];
  /** Formats the label for a given (visible) milestone id. */
  labelFor: (id: string) => string;
  /** Whether labels should be rotated/truncated (long, unabbreviated text). */
  rotate: boolean;
  /** Which strategy was used — exposed for tests/telemetry, not rendering. */
  mode: TickLabelMode;
}

/** Estimated px width a single rotated tick label needs to stay legible.
 * Used only as an input to the width/count skip-factor formula — not a
 * stand-in for a fixed skip number. */
const MIN_TICK_LABEL_PX = 32;

const MAX_LABEL_CHARS = 12;

function truncate(name: string): string {
  return name.length > MAX_LABEL_CHARS ? name.slice(0, MAX_LABEL_CHARS - 1) + '…' : name;
}

function extractYear(date: string | null | undefined): string | null {
  if (!date) return null;
  const match = /^(\d{4})-\d{2}-\d{2}/.exec(date);
  return match ? match[1] : null;
}

/**
 * Decides which milestones get a visible X-axis tick/label.
 *
 * - If everything fits (`count <= maxTicks` for the given width), show all.
 * - Otherwise, if every milestone has a resolvable year, group ticks by
 *   year (one label per year — short, unrotated, always legible).
 * - Otherwise, fall back to an evenly-spaced skip factor derived from
 *   `availableWidthPx / MIN_TICK_LABEL_PX` so the number of visible labels
 *   scales with the chart's actual rendered width, not a hard-coded count.
 */
export function computeXAxisTickPlan(
  milestones: TickableMilestone[],
  availableWidthPx: number
): XAxisTickPlan {
  const defaultLabelFor = (id: string): string => {
    const m = milestones.find((ms) => ms.id === id);
    return truncate(m?.name ?? id);
  };

  if (milestones.length === 0) {
    return { tickIds: [], labelFor: defaultLabelFor, rotate: false, mode: 'all' };
  }

  const maxTicks = Math.max(1, Math.floor(availableWidthPx / MIN_TICK_LABEL_PX));

  if (milestones.length <= maxTicks) {
    return {
      tickIds: milestones.map((m) => m.id),
      labelFor: defaultLabelFor,
      rotate: true,
      mode: 'all',
    };
  }

  const allYearsResolved = milestones.every((m) => extractYear(m.date) !== null);

  if (allYearsResolved) {
    const tickIds: string[] = [];
    const yearById = new Map<string, string>();
    let lastYear: string | null = null;
    milestones.forEach((m) => {
      const year = extractYear(m.date) as string;
      if (year !== lastYear) {
        tickIds.push(m.id);
        yearById.set(m.id, year);
        lastYear = year;
      }
    });
    return {
      tickIds,
      labelFor: (id) => yearById.get(id) ?? defaultLabelFor(id),
      rotate: false,
      mode: 'year',
    };
  }

  const skip = Math.ceil(milestones.length / maxTicks);
  const tickIds = milestones.filter((_, i) => i % skip === 0).map((m) => m.id);
  return { tickIds, labelFor: defaultLabelFor, rotate: true, mode: 'skip' };
}
